'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowBigRight, ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { MAGAZINE_SALE_STATUS_LIST } from '@/constants/constants';
import { Magazine, MagazinePage, MagazineSaleStatus } from '@/types/magazines';
import { TaxRate } from '@/types/taxRate';
import { formatMagazinePrice } from '@/utils/formatters/priceFormatter';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';
import TruncateText from '@/components/TruncateText';
import 'keen-slider/keen-slider.min.css';

interface MagazineDetailUnitProps {
    magazine: Magazine;
    magazine_pages: MagazinePage[];
    taxRates: TaxRate[] | null;
}

export default function MagazineDetailUnit({ magazine, magazine_pages, taxRates }: MagazineDetailUnitProps) {
    const [current, setCurrent] = useState(1);
    const total = magazine_pages.length;
    const hasEyecatch = Boolean(magazine?.eyecatch_public_url);
    const INQUIRY_CATEGORY_CD_FOR_PURCHASE_DIVE = '12';

    const sliderAnchorRef = useRef<HTMLDivElement | null>(null);
    const handleEyecatchClick = () => {
        sliderAnchorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(
        {
            loop: true,
            slides: {
                perView: 'auto',
                spacing: 16,
            },
            renderMode: 'performance',
            initial: 0,
        },
        [
            (s) => {
                s.on('created', () => setCurrent(s.track.details.rel + 1));
                s.on('slideChanged', () => setCurrent(s.track.details.rel + 1));
            },
        ],
    );

    const taxRate = taxRates?.find((rate) => rate.tax_category === magazine.tax_category);
    const saleStatusLabel = MAGAZINE_SALE_STATUS_LIST.find((item) => item.value === magazine.sale_status)?.label;

    return (
        <>
            <div className="w-full rounded-none md:rounded-xl bg-color-white-base md:shadow-full pb-12">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-4 md:p-6">
                    <div className="shrink-0 w-full md:w-[120px] lg:w-[140px]">
                        <div
                            onClick={handleEyecatchClick}
                            className="relative w-full aspect-[600/734] overflow-hidden rounded-lg ring-1 ring-black/10 bg-color-sl-cg-no-license-agreement-bg cursor-pointer"
                        >
                            {hasEyecatch ? (
                                <motion.img
                                    src={magazine.eyecatch_public_url}
                                    variants={{
                                        initial: { scale: 1 },
                                        hovered: { scale: 1.01 },
                                    }}
                                    transition={{ type: 'spring', stiffness: 300 }}
                                    className="w-full h-full rounded-lg"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <IconAdaptive
                                        Icon={ImageOff}
                                        strokeWidth={1.5}
                                        baseSize={30}
                                        breakpoints={{ md: 30 }}
                                        className="text-color-co-gray-dark"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center flex-1 min-w-0">
                        {magazine.title_prefix && (
                            <div className="font-corp font-bold text-sm sm:texte-base md:text-lg text-color-base-layout-logo leading-snug line-clamp-2">
                                <TruncateText text={magazine.title_prefix} maxLength={60} breakpoints={{ md: 80 }} />
                            </div>
                        )}
                        <div className="font-corp font-bold text-xl md:text-2xl text-color-base-layout-logo leading-snug line-clamp-2">
                            <TruncateText text={magazine.title} maxLength={60} breakpoints={{ md: 80 }} />
                        </div>
                        <div className="flex flex-col text-sm text-color-base mt-2">
                            {magazine.sale_status === MagazineSaleStatus.on_sale && (
                                <div className="font-corp text-xs text-color-co-gray-dark space-y-1">
                                    {magazine.supervisor_name && (
                                        <div className="inline-block px-2.5 py-0.5 rounded-full bg-color-base text-color-white-base mb-3">
                                            監修：{magazine.supervisor_name}
                                        </div>
                                    )}
                                    {magazine.sale_status == MagazineSaleStatus.on_sale && magazine.price != null && (
                                        <div className="font-corp text-color-base text-sm">
                                            {formatMagazinePrice(magazine.price, taxRate)}
                                        </div>
                                    )}
                                    <div className="font-notojp font-medium text-xs tracking-wide">
                                        {<p>形式：{magazine.magazine_format}</p>}
                                        {magazine.total_pages && <p>総ページ数：{magazine.total_pages}</p>}
                                        {magazine.jan_code && <p>JANコード：{magazine.jan_code}</p>}
                                        {magazine.model_number && <p>型番：{magazine.model_number}</p>}
                                    </div>
                                </div>
                            )}
                            {magazine.sale_status !== MagazineSaleStatus.on_sale && (
                                <div className="inline-block">
                                    <span
                                        className={`
                    px-2 py-0.5 rounded-full text-xs font-corp
                    ${
                        magazine.sale_status === MagazineSaleStatus.pre_sale
                            ? 'bg-color-co-gray-light text-color-co-gray-dark'
                            : 'bg-color-co-gray-light text-color-co-gray-dark'
                    }
                `}
                                    >
                                        {saleStatusLabel}
                                    </span>
                                </div>
                            )}
                            {magazine.content && (
                                <span className="hidden lg:block font-corp pt-2 text-sm text-color-base">
                                    {magazine.content}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="hidden lg:block px-6">
                        <Link
                            href={`/inquiry/form/business?category_cd=${INQUIRY_CATEGORY_CD_FOR_PURCHASE_DIVE}`}
                            target="_blank"
                            prefetch={false}
                        >
                            <ButtonGAdaptive
                                type="button"
                                label="ご購入に関するお問い合わせ"
                                description="inquiry"
                                icon={ArrowBigRight}
                            />
                        </Link>
                    </div>
                </div>
                <div className="lg:hidden w-full px-4 pt-2 pb-4">
                    <div className="flex flex-col text-sm text-color-base">
                        <div className="font-corp">{magazine.content}</div>
                    </div>
                    <div className="mt-6 text-center">
                        <Link
                            href={`/inquiry/form/business?category_cd=${INQUIRY_CATEGORY_CD_FOR_PURCHASE_DIVE}`}
                            target="_blank"
                            prefetch={false}
                        >
                            <ButtonGAdaptive
                                type="button"
                                label="ご購入に関するお問い合わせ"
                                description="inquiry"
                                icon={ArrowBigRight}
                                size="xs"
                                breakpoints={{ md: 'xl' }}
                            />
                        </Link>
                    </div>
                </div>
                <div ref={sliderAnchorRef} className="relative overflow-visible p-4 scroll-mt-20 md:scroll-mt-0">
                    <div ref={sliderRef} className="keen-slider bg-black py-2 cursor-ew-resize">
                        {magazine_pages.map((page, idx) => (
                            <div key={idx} className="keen-slider__slide relative min-w-[80%] xl:min-w-[65%]">
                                {page.magazine_page_public_url ? (
                                    <motion.img
                                        src={page.magazine_page_public_url}
                                        alt={`magazine-page-${idx + 1}`}
                                        className="w-full aspect-[1200/734]"
                                        variants={{
                                            initial: { scale: 1 },
                                            hovered: { scale: 1.02 },
                                        }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    />
                                ) : (
                                    <div className="w-full aspect-[1200/734] flex items-center justify-center">
                                        <span className="text-white text-xl md:text-2xl font-medium">
                                            Coming soon...
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {total > 0 && (
                        <div className="absolute inset-x-0 -bottom-[15px] z-10">
                            <div className="mx-auto w-full grid grid-cols-[auto,1fr,auto] items-center px-2">
                                <button
                                    className="justify-self-start"
                                    onClick={() => slider.current?.prev()}
                                    aria-label="前のページへ"
                                >
                                    <IconAdaptive
                                        Icon={ChevronLeft}
                                        baseSize={30}
                                        breakpoints={{ md: 30 }}
                                        className="text-color-base"
                                    />
                                </button>
                                <div
                                    className="font-cantarell text-center text-color-base text-sm md:text-base"
                                    aria-live="polite"
                                    aria-atomic="true"
                                >
                                    {current} / {total}
                                </div>
                                <button
                                    className="justify-self-end"
                                    onClick={() => slider.current?.next()}
                                    aria-label="次のページへ"
                                >
                                    <IconAdaptive
                                        Icon={ChevronRight}
                                        baseSize={30}
                                        breakpoints={{ md: 30 }}
                                        className="text-color-base"
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
